/* eslint-disable @typescript-eslint/no-explicit-any */
import { UntypedFormGroup } from '@angular/forms';
import { OptionModel } from './option.model';

export interface DynamicForm {
  name: string;
  steps: Array<DynamicFormStep>;
  specialValues: {
    [x: string]: SpecialValueAccessor;
  };
}

export interface SpecialValueAccessor {
  controlName: string;
  sectionName: string;
  valuePropertyName: string;
}

export interface DynamicFormStep {
  name: string;
  instructions?: string;
  sections: Array<DynamicFormSection>;
  // Removed in favor of using AppCommentsService.StepsVerified to track this.
  // hasAreaOfConcern?: boolean;
  hasContractorComments?: boolean;
  // Relevant for SO Users only. Checked if the step has been verified by the SO Agency.
  soVerified?: boolean;
}

export interface DynamicFormSection {
  name: string;
  formName: string;
  formData: Array<DynamicFormData>;
  commentsEnabled: boolean;
  formInformation?: string;
  /**
   * This property is not parsed from the original questionnaire JSON, but rather added on in the vendorAppViewComponent
   * This is only true whenever the app is RETURNED and the section is ALREADY VERIFIED.
   */
  readonly?: boolean;
  // comments?: AppComment;
}

export interface DynamicFormData {
  controlName: string;
  controlType: DynamicControlType;
  controlLabel: string;
  prefixLabel?: string;
  yearLabel?: string;
  currentValue?: any;
  placeholder?: string;
  hint?: string;
  inputType?: string;
  headerType?: string;
  boldText?: boolean;
  options?: OptionModel<any>[];
  validators?: {
    required?: boolean;
    requiredFalse?: boolean;
    requiredTrue?: boolean;
    minlength?: number;
    maxlength?: number;
    minAmount?: number;
    maxAmount?: number;
  };
  attributes?: {
    mask?: string;
  };
  hidden: boolean;
  visibleIf?: DynamicCondition[] | DynamicCondition;
  /** If true the control is created but not rendered in the app, so calculations can still be performed on it. */
  invisible?: boolean;
  /** If true, only the agency user will be able to view this control. */
  agencyOnly?: boolean;
  /** If true, display this control inline with the rest of the content. */
  inline?: boolean;
  /** Used for the button control type to display a link. */
  href?: string;
  /** Currently used for the button control to always display even if the section is readonly. */
  alwaysEnabled?: boolean;
  /** Used for the inputGrid control type to display a grid of inputs. */
  inputGridFields?: DynamicFormData[];
  /** Used to label the type of row we're filling out - eg. Citation, Incident, Classification/Certification, etc. */
  inputGridTypeLabel?: string;
  scoring?: {
    [x: string]: {
      totalPoints: number;
      conditions: {
        score: number;
        requiredValue: number;
        requiredOperator: DynamicOperator;
      }[];
    };
  };
}

export enum DynamicControlType {
  Input = 'input',
  Phone = 'telephone',
  Radio = 'radio',
  Checkbox = 'checkbox',
  Dropdown = 'dropdown',
  StatesDropdown = 'statesDropdown',
  BusinessTypeDropdown = 'businessTypeDropdown',
  Address = 'address',
  Currency = 'currency',
  CurrencyWhole = 'currencyWhole',
  Date = 'date',
  TextArea = 'textArea',
  ChipSelect = 'chipSelect',
  ClassificationsSelect = 'classificationSelect',
  CertificationsSelect = 'certificationsSelect',
  AgencySelect = 'agencySelect',
  Header = 'header',
  Paragraph = 'paragraph',
  FileUpload = 'fileUpload',
  CSLBLicense = 'cslbLicense',
  Comments = 'comments',
  SectionComments = 'sectionComments',
  ContactName = 'contactName',
  Projects = 'projects',
  /** @deprecated Use DirNumber instead. */
  DirExp = 'dirExp',
  DirNumber = 'dirNumber',
  FinancialCapacity = 'financialCapacity',
  Signature = 'signature',
  LausdProjects = 'lausdProjects',
  EmrDetails = 'emrDetails',
  /** A 'god-component' that is used to display a grid of inputs. */
  InputGrid = 'inputGrid',
  /** A disabled field that can be set to a value by other control customization,
   * and that value can be used for visibleIf calculations. Usually should be set to invisible, but doesn't have to be. */
  Helper = 'helper',
  Score = 'score',
  OshaCitationDetails = 'oshaCitationDetails',
  LausdCertificationSection = 'lausdCertificationSection',
  OshaIncidenceRates = 'oshaIncidenceRates',
  Button = 'button',
  RevenueInformation = 'revenueInformation',
  LausdResourceTemplate = 'lausdResourceTemplate',
}

export interface DynamicCondition {
  controlName?: string;
  operator: DynamicOperator;
  value: any;
}

export enum DynamicOperator {
  // Any operators
  Equals = 'equals',
  DoesNotEqual = 'doesNotEqual',

  // String operators
  TextLike = 'textLike',
  TextNotLike = 'textNotLike',

  // Numeric operators
  GreaterThan = 'greaterThan',
  GreaterThanOrEqual = 'greaterThanOrEqual',
  LessThan = 'lessThan',
  LessThanOrEqual = 'lessThanOrEqual',

  // Array operators
  Contains = 'contains',
  DoesNotContain = 'doesNotContain',
  HasSelectedVal = 'hasselectedval',
  hasSelected = 'hasSelected',
}

/** DEPRECATED */
export interface DynamicFormValues {
  forms: any[];
}

export interface DynamicFormValuesSections {
  [x: string]: UntypedFormGroup;
}

export interface FormError {
  formControlName?: string;
  errorFixed?: boolean;
  formName?: string;
}
